// src/components/Dashboard/UpcomingBirthdays.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import notificationService from '../../services/notification.service';

const MAX_INLINE = 3;

const buildWishLink = (person) => {
  const digits = (person.phone || '').replace(/\D/g, '');
  if (!digits) return null;
  const fullPhone = digits.length === 10 ? `91${digits}` : digits;
  const message = `🎉 Happy Birthday, ${person.firstName}! Wishing you a fantastic year ahead filled with success, happiness and good health. Have a wonderful day! 🎂🎈`;
  return `https://wa.me/${fullPhone}?text=${encodeURIComponent(message)}`;
};

const isToday = (dob) => {
  const d = new Date(dob), t = new Date();
  return d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
};

const fmt = (dob) => new Date(dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

const BirthdayRow = ({ p }) => {
  const wishLink = buildWishLink(p);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 38, height: 38, borderRadius: '50%', background: '#e8f5e9',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#2e7d32', fontWeight: 700, fontSize: 14, flexShrink: 0,
      }}>
        {p.avatar
          ? <img src={p.avatar} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover' }} />
          : `${p.firstName?.[0] || ''}${p.lastName?.[0] || ''}`.toUpperCase()}
      </div>
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#333' }}>
          {p.firstName} {p.lastName}
        </p>
        <p style={{ margin: 0, fontSize: 12, color: '#888' }}>{p.employeeCode || ''}</p>
      </div>
      <span style={{
        fontSize: 12, fontWeight: 600,
        color: isToday(p.dateOfBirth) ? '#e53935' : '#2e7d32',
        background: isToday(p.dateOfBirth) ? '#fce4ec' : '#e8f5e9',
        padding: '3px 10px', borderRadius: 12, whiteSpace: 'nowrap',
      }}>
        {isToday(p.dateOfBirth) ? '🎉 Today' : fmt(p.dateOfBirth)}
      </span>
      {wishLink ? (
        <a href={wishLink} target="_blank" rel="noopener noreferrer"
          style={{
            padding: '5px 10px', background: '#25d366', color: '#fff', borderRadius: 6,
            fontSize: 11, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
          💬 Wish
        </a>
      ) : (
        <span style={{ fontSize: 10, color: '#adb5bd', flexShrink: 0 }}>No phone</span>
      )}
    </div>
  );
};

const BirthdayModal = ({ list, onClose }) => (
  <div onClick={onClose} style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  }}>
    <div onClick={e => e.stopPropagation()} style={{
      background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480,
      maxHeight: '80vh', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>🎂 Upcoming Birthdays</h5>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6c757d' }}>✕</button>
      </div>
      <div style={{ padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {list.map(p => <BirthdayRow key={p.id} p={p} />)}
      </div>
    </div>
  </div>
);

const UpcomingBirthdays = () => {
  const { isSuperAdmin } = useAuth();
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const companyId = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : null;
    notificationService.getUpcomingBirthdays(companyId)
      .then(res => { if (res.success) setList(res.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isSuperAdmin]);

  return (
    <div className="db-card">
      <div className="db-card-header">
        <h6 className="db-card-title">🎂 Upcoming Birthdays</h6>
      </div>
      {loading ? (
        <p style={{ color: '#adb5a0', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Loading...</p>
      ) : list.length === 0 ? (
        <p style={{ color: '#adb5a0', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
          No birthdays in the next 7 days
        </p>
      ) : list.length <= MAX_INLINE ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {list.map(p => <BirthdayRow key={p.id} p={p} />)}
        </div>
      ) : (
        <>
          <div
            onClick={() => setShowModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 10, padding: '12px 16px',
            }}
          >
            <span style={{ fontSize: 20 }}>🎂</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#e65100' }}>
                {list.length} birthdays this week
              </p>
              <p style={{ margin: 0, fontSize: 11, color: '#6c757d' }}>Tap to view and send wishes</p>
            </div>
            <span style={{ color: '#e65100' }}>›</span>
          </div>
          {showModal && <BirthdayModal list={list} onClose={() => setShowModal(false)} />}
        </>
      )}
    </div>
  );
};

export default UpcomingBirthdays;